import { useQuery } from '@tanstack/react-query';
import { Star, X } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { useUiStore } from '../store/ui';
import { fmtDate } from '../lib/dates';

const LEVEL_LABEL: Record<string, { label: string; tone: string }> = {
  acquis: { label: 'Acquis', tone: 'bg-green-50 text-green-700 border-green-200' },
  en_cours: { label: 'En cours', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  a_revoir: { label: 'À revoir', tone: 'bg-red-50 text-red-700 border-red-200' },
};

export default function LivretModal() {
  const studentId = useUiStore((s) => s.livretStudentId);
  const close = useUiStore((s) => s.closeLivret);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['student', studentId],
    queryFn: () => instructorApi.getStudent(studentId as string),
    enabled: !!studentId,
  });

  if (!studentId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="text-slate-900 text-lg font-semibold">
              Livret d'apprentissage
            </h2>
            {data && (
              <p className="text-xs text-slate-500">
                {data.firstName} {data.lastName} · {data.drivingHoursDone}/
                {data.drivingHoursTarget} h de conduite
              </p>
            )}
          </div>
          <button
            onClick={close}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {isLoading && <p className="text-slate-500">Chargement du livret…</p>}
          {isError && (
            <p className="text-red-600">
              Impossible de charger le livret : {(error as Error).message}
            </p>
          )}

          {data && data.bilans.length === 0 && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500 text-sm">
              Aucun bilan enregistré pour cet élève.
            </div>
          )}

          {data?.bilans.map((b) => (
            <div
              key={b.id}
              className="rounded-2xl border border-slate-200 p-4 shadow-sm"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-900">
                  {fmtDate(b.lessonDate ?? b.createdAt)}
                </span>
                <div className="flex items-center gap-2">
                  {b.readyForExam && (
                    <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 text-[11px] font-semibold">
                      Prêt examen
                    </span>
                  )}
                  {b.rating != null && (
                    <span className="flex items-center gap-0.5 text-amber-500 text-xs">
                      <Star size={13} className="fill-amber-400 text-amber-400" />
                      {b.rating}/5
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-600 whitespace-pre-line">{b.summary}</p>
              {b.competencies.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {b.competencies.map((c, i) => {
                    const meta = LEVEL_LABEL[c.level] ?? {
                      label: c.level,
                      tone: 'bg-slate-50 text-slate-600 border-slate-200',
                    };
                    return (
                      <span
                        key={`${c.label}-${i}`}
                        className={`px-2.5 py-1 rounded-full border text-[11px] font-medium ${meta.tone}`}
                      >
                        {c.label} · {meta.label}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
